/**
 * Debug store — captures real-time voice session events (tool calls, moderation,
 * transcripts) for developer inspection via the DevDebugPanel.
 *
 * Events are only collected when debug mode is active (`?dev=1` or __DEV__).
 */

import { create } from "zustand";
import { Platform } from "react-native";

export type DebugEventType =
  | "toolUse"
  | "toolResult"
  | "moderationBlocked"
  | "slowModeVerdict"
  | "ipSanitizer"
  | "kidTranscript"
  | "agentResponse";

export interface DebugEvent {
  id: number;
  type: DebugEventType;
  timestamp: number;
  // Tool use
  toolName?: string;
  action?: string;
  contentTier?: string;
  topics?: string[];
  // Tool result / slow mode
  isAppropriate?: boolean;
  reason?: string;
  latencyMs?: number;
  verdict?: string;
  // Tool result message
  error?: string;
  message?: string;
  // Moderation blocked
  elementType?: string;
  elementId?: string;
  stage?: string;
  // Transcript / response text
  text?: string;
  // Raw payload for inspection
  raw?: unknown;
}

interface DebugState {
  enabled: boolean;
  panelOpen: boolean;
  events: DebugEvent[];
  nextId: number;

  setEnabled: (enabled: boolean) => void;
  togglePanel: () => void;
  addEvent: (event: Omit<DebugEvent, "id" | "timestamp">) => void;
  clear: () => void;
}

const MAX_EVENTS = 200;

function isDevModeFromUrl(): boolean {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("dev") === "1";
  } catch {
    return false;
  }
}

export const useDebugStore = create<DebugState>((set, get) => ({
  enabled: typeof __DEV__ !== "undefined" ? __DEV__ : isDevModeFromUrl(),
  panelOpen: false,
  events: [],
  nextId: 1,

  setEnabled: (enabled) => set({ enabled }),

  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),

  addEvent: (event) => {
    if (!get().enabled) return;
    set((s) => {
      const newEvents = [
        ...s.events,
        { ...event, id: s.nextId, timestamp: Date.now() },
      ];
      // Keep only the latest MAX_EVENTS
      const trimmed = newEvents.length > MAX_EVENTS
        ? newEvents.slice(newEvents.length - MAX_EVENTS)
        : newEvents;
      return { events: trimmed, nextId: s.nextId + 1 };
    });
  },

  clear: () => set({ events: [], nextId: 1 }),
}));
